import dayjs from 'dayjs';
import { getReferenceString, parseReference, PatchOperation } from '@medplum/core';
import { CareTeam, Patient, Practitioner, Reference, Task } from '@medplum/fhirtypes';
import { ORBIT_PROVIDER_TASK_GROUP_IDENTIFIER, PROVIDER_ROLE_CODING_MAP } from './codings';
import { TASK_COMPLETED_BY_EXTENSION } from './extensions';
import { PROVIDER_TASK_PERFORMER_ROLES, ProviderRole, TaskStatus } from './constants';
import { CreateClinicalTaskInput } from './task-management';

/**
 * Read path for clinical tasks (the Tasks written by the `create-clinical-task`
 * bot). Every query here is scoped by `group-identifier = orbit-provider-task`,
 * which keeps clinical tasks fully disjoint from the inbox-notification Task
 * family (resource-assignment / provider-mention / general-notification).
 *
 * That group scoping is also what makes `status = completed` unambiguous:
 * the notification pipeline reads `completed` as "archived", but no clinical
 * query ever sees notification Tasks and vice versa, so clinical completion
 * can use the plain FHIR status without colliding with the archive concept.
 *
 * Domain note: `Task.executionPeriod.end` carries the clinical task's DUE
 * DATE (see create-clinical-task). It is optional — helpers here must never
 * assume it is present.
 *
 * Deletion is SOFT: "Delete task" sets `status = cancelled` rather than
 * deleting the resource, keeping the audit trail on a clinical record.
 * Cancelled tasks fall out of every query here because all of them filter
 * to {@link OPEN_CLINICAL_TASK_STATUSES}.
 */

/**
 * Token for FHIR token search on Task.groupIdentifier. VALUE-ONLY, matching
 * every other group-identifier query in the app (notification-queries.ts,
 * useNotificationsCount): the hosted server fails to match system|value
 * qualified tokens on newly indexed writes, and the value is unique enough
 * that system qualification adds nothing. The access-policy rules use the
 * same value-only form.
 */
export const CLINICAL_TASK_GROUP_IDENTIFIER_TOKEN = ORBIT_PROVIDER_TASK_GROUP_IDENTIFIER.value ?? 'orbit-provider-task';

/** Statuses that count as "open" on the patient Tasks tab. */
export const OPEN_CLINICAL_TASK_STATUSES: TaskStatus[] = [TaskStatus.READY, TaskStatus.IN_PROGRESS];

/**
 * One page of open clinical tasks is plenty at patient scale, but the cap is
 * silent: a patient with more open tasks than this loses rows with no
 * indication. Revisit before any list that aggregates across patients.
 */
export const CLINICAL_TASKS_PAGE_SIZE = 100;

/**
 * Search params for a patient's open clinical tasks, for
 * `medplum.searchResources('Task', …)`. Sorting is done client-side via
 * {@link compareClinicalTasks} — sort order here would not be honored by
 * MockClient in tests anyway.
 */
export function buildPatientClinicalTasksSearch(patient: Patient | Reference<Patient>): Record<string, string> {
  return {
    'group-identifier': CLINICAL_TASK_GROUP_IDENTIFIER_TOKEN,
    patient: requirePatientRef(patient),
    status: OPEN_CLINICAL_TASK_STATUSES.join(','),
    _count: String(CLINICAL_TASKS_PAGE_SIZE),
  };
}

function requirePatientRef(patient: Patient | Reference<Patient>): string {
  const ref = getReferenceString(patient);
  if (!ref) {
    throw new Error('Patient reference is required to query clinical tasks');
  }
  return ref;
}

/**
 * Subscription criteria string for the same population. Status is deliberately
 * omitted: a completion flips a task OUT of the open set, and the subscription
 * must still fire on that transition for the list to drop the row.
 */
export function buildPatientClinicalTasksSubscriptionCriteria(patient: Patient | Reference<Patient>): string {
  const params = new URLSearchParams({
    'group-identifier': CLINICAL_TASK_GROUP_IDENTIFIER_TOKEN,
    patient: requirePatientRef(patient),
  });
  return `Task?${params.toString()}`;
}

/** The clinical task's due date (`executionPeriod.end`), if one was set. */
export function getClinicalTaskDueDate(task: Task): string | undefined {
  return task.executionPeriod?.end;
}

/**
 * True when the task's due date is before TODAY (day granularity, local
 * time): a task due today is due, not overdue. This matches the inbox
 * helpers (inbox-sort.ts / inbox-task-filters.ts) — due dates are dates,
 * not timestamps. A task WITHOUT a due date is never overdue.
 */
export function isClinicalTaskOverdue(task: Task, now: Date = new Date()): boolean {
  const due = getClinicalTaskDueDate(task);
  if (!due) {
    return false;
  }
  return dayjs(due).isBefore(dayjs(now), 'day');
}

export interface ClinicalTaskDueDescription {
  /** One-line display label, e.g. `Today`, `Tomorrow`, `In 4 days`, `3 days ago`, `Apr 12`. */
  label: string;
  overdue: boolean;
  isToday: boolean;
}

/**
 * Due-date display per the design system's date-time canon (Close-style),
 * adapted to DAY granularity because due dates are dates, not timestamps —
 * the minute/hour bands don't apply. Within ±6 days: relative (`Today`,
 * `Tomorrow`, `In N days`, `N days ago`); beyond: the shortened absolute
 * date (`Apr 12`, or `Mar 22, 2024` across a year boundary). Never
 * "X months ago". Pair with a full-date tooltip at the render site.
 */
export function describeClinicalTaskDue(dueIso: string, now: Date = new Date()): ClinicalTaskDueDescription {
  const due = dayjs(dueIso).startOf('day');
  const today = dayjs(now).startOf('day');
  const diffDays = due.diff(today, 'day');

  if (diffDays === 0) {
    return { label: 'Today', overdue: false, isToday: true };
  }
  if (diffDays === 1) {
    return { label: 'Tomorrow', overdue: false, isToday: false };
  }
  if (diffDays > 1 && diffDays <= 6) {
    return { label: `In ${diffDays} days`, overdue: false, isToday: false };
  }
  if (diffDays < 0 && diffDays >= -6) {
    return { label: diffDays === -1 ? '1 day ago' : `${-diffDays} days ago`, overdue: true, isToday: false };
  }
  const format = due.year() === today.year() ? 'MMM D' : 'MMM D, YYYY';
  return { label: due.format(format), overdue: diffDays < 0, isToday: false };
}

/**
 * Display order for the patient Tasks tab: overdue first (most overdue on
 * top), then dated tasks by due date ascending, then undated tasks by
 * authoredOn descending (newest first), with a final id tiebreak so the
 * order is total and stable across refreshes.
 *
 * Callers sorting a list MUST capture one `now` and pass it to every
 * comparison — re-evaluating the default per comparison lets a task cross
 * its due date mid-sort, which violates the comparator contract.
 */
export function compareClinicalTasks(a: Task, b: Task, now: Date = new Date()): number {
  const aOverdue = isClinicalTaskOverdue(a, now);
  const bOverdue = isClinicalTaskOverdue(b, now);
  if (aOverdue !== bOverdue) {
    return aOverdue ? -1 : 1;
  }

  const aDue = getClinicalTaskDueDate(a);
  const bDue = getClinicalTaskDueDate(b);
  if (aDue && bDue) {
    const byDue = new Date(aDue).getTime() - new Date(bDue).getTime();
    if (byDue !== 0) {
      return byDue;
    }
  } else if (aDue !== bDue) {
    return aDue ? -1 : 1;
  }

  const aAuthored = a.authoredOn ? new Date(a.authoredOn).getTime() : 0;
  const bAuthored = b.authoredOn ? new Date(b.authoredOn).getTime() : 0;
  if (aAuthored !== bAuthored) {
    return bAuthored - aAuthored;
  }

  // Final id tiebreak: the search sends no _sort, so without this, ties keep
  // whatever order the server returned and rows can shuffle between refreshes.
  return (a.id ?? '').localeCompare(b.id ?? '');
}

/**
 * The user-editable fields of a clinical task — the same shape the create
 * form collects (minus the patient, which is immutable). In an EDIT, an
 * absent `description` or `dueDate` means REMOVE, not "leave unchanged".
 */
export type ClinicalTaskEditFields = Omit<CreateClinicalTaskInput, 'patient'>;

/** The task's individual assignee: its owner, when that owner is a Practitioner. */
export function getClinicalTaskAssignee(task: Task): Reference<Practitioner> | undefined {
  if (!task.owner?.reference) {
    return undefined;
  }
  try {
    const [resourceType] = parseReference(task.owner);
    return resourceType === 'Practitioner' ? (task.owner as Reference<Practitioner>) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Reads a task back into form fields for prefilling the edit form —
 * the inverse of create-clinical-task's field mapping (title↔description,
 * high↔urgent, dueDate↔executionPeriod.end, performer↔performerType coding,
 * rich-text JSON string↔note[0].text).
 *
 * Lossy by design for values no writer of ours produces: a priority that is
 * neither `urgent` nor `routine` (e.g. `asap`) prefills as routine and a
 * subsequent save writes that downgrade.
 */
export function getClinicalTaskEditFields(task: Task): ClinicalTaskEditFields {
  const assignee = getClinicalTaskAssignee(task);
  const code = task.performerType?.[0]?.coding?.[0]?.code as ProviderRole | undefined;
  const performer = assignee
    ? undefined
    : code && PROVIDER_TASK_PERFORMER_ROLES.includes(code)
      ? code
      : ProviderRole.PATIENT_COORDINATOR;

  return {
    title: task.description ?? '',
    performer,
    assignee,
    priority: task.priority === 'urgent' ? 'high' : 'routine',
    description: task.note?.[0]?.text,
    dueDate: getClinicalTaskDueDate(task),
  };
}

/**
 * JSONPatch ops applying an edit to a clinical task. Ops only — the caller
 * is expected to prepend its versionId `test` guard and lastModified update
 * (see useGuardedTaskPatch), the same protocol as completion.
 *
 * `note` is replaced wholesale: clinical tasks are created with at most one
 * note and nothing else writes to it, so preserving hypothetical extra
 * entries isn't worth the op complexity.
 *
 * Due dates are not re-validated against "now" here (create-clinical-task
 * rejects past dates at creation): an overdue task must remain editable
 * without being forced to pick a new due date.
 */
/** Options for {@link buildClinicalTaskEditOps}. */
export interface ClinicalTaskEditOptions {
  nowIso?: string;
  /**
   * The patient's CareTeam — REQUIRED when the edit switches an individually
   * assigned task back to a role, because ownership must return to the team.
   * Pass `null` when the patient has no CareTeam: the role task is then left
   * unowned, the shape create-clinical-task writes for such a patient.
   */
  careTeam?: Reference<CareTeam> | null;
}

/**
 * JSONPatch ops applying an edit to a clinical task. Ops only — the caller
 * is expected to prepend its versionId `test` guard and lastModified update
 * (see useGuardedTaskPatch), the same protocol as completion.
 *
 * `note` is replaced wholesale: clinical tasks are created with at most one
 * note and nothing else writes to it, so preserving hypothetical extra
 * entries isn't worth the op complexity.
 *
 * Due dates are not re-validated against "now" here (create-clinical-task
 * rejects past dates at creation): an overdue task must remain editable
 * without being forced to pick a new due date.
 */
export function buildClinicalTaskEditOps(
  task: Task,
  fields: ClinicalTaskEditFields,
  options: ClinicalTaskEditOptions = {}
): PatchOperation[] {
  const nowIso = options.nowIso ?? new Date().toISOString();
  if (!fields.performer === !fields.assignee) {
    throw new Error('exactly one of performer/assignee is required');
  }

  const ops: PatchOperation[] = [
    { op: task.description !== undefined ? 'replace' : 'add', path: '/description', value: fields.title.trim() },
    {
      op: task.priority !== undefined ? 'replace' : 'add',
      path: '/priority',
      value: fields.priority === 'high' ? 'urgent' : 'routine',
    },
  ];

  if (fields.assignee) {
    // Individual assignment: the practitioner becomes owner; the role goes away.
    if (task.performerType !== undefined) {
      ops.push({ op: 'remove', path: '/performerType' });
    }
    ops.push({ op: task.owner !== undefined ? 'replace' : 'add', path: '/owner', value: fields.assignee });
  } else if (fields.performer) {
    ops.push({
      op: task.performerType !== undefined ? 'replace' : 'add',
      path: '/performerType',
      value: [{ coding: [PROVIDER_ROLE_CODING_MAP[fields.performer]] }],
    });
    if (getClinicalTaskAssignee(task)) {
      if (options.careTeam === undefined) {
        throw new Error('careTeam is required when switching an individually assigned task back to a role');
      }
      ops.push(
        options.careTeam ? { op: 'replace', path: '/owner', value: options.careTeam } : { op: 'remove', path: '/owner' }
      );
    }
  }

  if (fields.description) {
    ops.push({
      op: task.note !== undefined ? 'replace' : 'add',
      path: '/note',
      value: [{ text: fields.description }],
    });
  } else if (task.note !== undefined) {
    ops.push({ op: 'remove', path: '/note' });
  }

  if (fields.dueDate) {
    if (task.executionPeriod !== undefined) {
      // `end` may be absent on externally-written periods (module contract);
      // rfc6902 `replace` would throw "Value required at path" there.
      ops.push({
        op: task.executionPeriod.end !== undefined ? 'replace' : 'add',
        path: '/executionPeriod/end',
        value: fields.dueDate,
      });
    } else {
      ops.push({ op: 'add', path: '/executionPeriod', value: { start: nowIso, end: fields.dueDate } });
    }
  } else if (task.executionPeriod !== undefined) {
    ops.push({ op: 'remove', path: '/executionPeriod' });
  }

  return ops;
}

/** Rows per request for the admin task log. */
export const TASK_LOG_PAGE_SIZE = 200;

/**
 * Hard ceiling on how many open tasks the admin log will page through, so a
 * runaway dataset can't hang the page. Open tasks are a bounded WORKING set
 * (they get completed), unlike the completed history, so full coverage below
 * this ceiling is realistic — and the page tells the user when it's hit.
 */
export const TASK_LOG_MAX_ROWS = 1000;

/**
 * One page of open clinical tasks across patients, for the admin task log.
 * `_offset` pages through the set; `_total=accurate` is what lets the page
 * report "showing N of M" instead of silently truncating. There is no
 * server-side sort by DUE DATE (the FHIR `period` param sorts on
 * executionPeriod.start), so ordering is applied client-side with
 * {@link compareClinicalTasks} over the full set.
 */
export function buildOpenClinicalTasksSearch(offset = 0): Record<string, string> {
  return {
    'group-identifier': CLINICAL_TASK_GROUP_IDENTIFIER_TOKEN,
    status: OPEN_CLINICAL_TASK_STATUSES.join(','),
    _count: String(TASK_LOG_PAGE_SIZE),
    _offset: String(offset),
    _total: 'accurate',
  };
}

/** ALL completed clinical tasks for one patient (the timeline's history view). */
export function buildPatientCompletedClinicalTasksSearch(
  patient: Patient | Reference<Patient>
): Record<string, string> {
  return {
    'group-identifier': CLINICAL_TASK_GROUP_IDENTIFIER_TOKEN,
    patient: requirePatientRef(patient),
    status: TaskStatus.COMPLETED,
    _count: String(TASK_LOG_PAGE_SIZE),
  };
}

/**
 * Clinical tasks completed on/after `sinceIso`, across patients. Uses the
 * `modified` search param (Task.lastModified) — the guarded patch protocol
 * stamps lastModified on every status write, so for a completed task it is
 * the completion time.
 */
export function buildRecentlyCompletedClinicalTasksSearch(sinceIso: string): Record<string, string> {
  return {
    'group-identifier': CLINICAL_TASK_GROUP_IDENTIFIER_TOKEN,
    status: TaskStatus.COMPLETED,
    modified: `ge${sinceIso}`,
    _count: String(TASK_LOG_PAGE_SIZE),
  };
}

/**
 * Who completed the task (valueReference of the task-completed-by
 * extension, stamped by useTaskCompletion). Absent on tasks completed
 * before the stamp shipped.
 */
export function getClinicalTaskCompletedBy(task: Task): Reference<Practitioner> | undefined {
  const extension = task.extension?.find((e) => e.url === TASK_COMPLETED_BY_EXTENSION);
  return extension?.valueReference as Reference<Practitioner> | undefined;
}

/**
 * Whether a user may COMPLETE this task (used by the patient tab and the
 * inbox): an individually assigned task only by that practitioner; a
 * role-assigned task only by holders of that role; ADMINS may complete
 * anything. EDITING is deliberately not gated — anyone can edit, including
 * reassigning to themselves. A task carrying neither assignment signal is
 * completable by anyone (defensive: gating it would make it permanently
 * un-completable).
 *
 * Client-side gating only — the access policies still allow the write, so
 * this is a workflow rule, not a security boundary.
 */
export function canCompleteClinicalTask(
  task: Task,
  user: { profileRef?: string; role?: ProviderRole; isAdmin?: boolean }
): boolean {
  if (user.isAdmin) {
    return true;
  }
  const assignee = getClinicalTaskAssignee(task);
  if (assignee?.reference) {
    return assignee.reference === user.profileRef;
  }
  const roleCode = task.performerType?.[0]?.coding?.[0]?.code as ProviderRole | undefined;
  if (roleCode) {
    return roleCode === user.role;
  }
  return true;
}

export interface ClinicalTaskPartition {
  /** Open tasks that are past their due date. */
  overdue: Task[];
  /** Open tasks that are not overdue (including undated ones). */
  open: Task[];
}

/** Splits open clinical tasks for the tab's "N open · N overdue" summary. */
export function partitionClinicalTasks(tasks: Task[], now: Date = new Date()): ClinicalTaskPartition {
  const partition: ClinicalTaskPartition = { overdue: [], open: [] };
  for (const task of tasks) {
    if (!OPEN_CLINICAL_TASK_STATUSES.includes(task.status as TaskStatus)) {
      continue;
    }
    if (isClinicalTaskOverdue(task, now)) {
      partition.overdue.push(task);
    } else {
      partition.open.push(task);
    }
  }
  return partition;
}
